import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { AffiliateNetworkService } from "@gen/tracker/v1/affiliate_network_connect";
import { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";
import type { ListNetworkNumbersResponse, ListNetworkStatusesResponse } from "@gen/tracker/v1/affiliate_network_pb";
import { DateRange, ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import { keepPreviousData } from "@tanstack/react-query";
import { fetchAll, useByIds } from "@/lib/services/catalog";
import { basisToProto } from "@/lib/services/analytics-service";
import type { ConversionBasis, DayRange } from "@/lib/period";

const client = createClient(AffiliateNetworkService);

export const networkKeys = {
  all: ["affiliate-networks"] as const,
  list: (params: ListNetworksParams) => [...networkKeys.all, "list", params] as const,
  detail: (id: string) => [...networkKeys.all, "detail", id] as const,
};

export interface ListNetworksParams {
  search?: string;
  ids?: string[];
  /** name | created_at; none = newest first. */
  sort?: ListSort;
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export function useListAffiliateNetworks(
  params: ListNetworksParams = {},
  options?: Partial<UseQueryOptions<{ items: AffiliateNetwork[]; total: number }>>,
) {
  return useQuery({
    queryKey: networkKeys.list(params),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.listAffiliateNetworks({
        search: params.search ?? "",
        statuses: [],
        ids: params.ids ?? [],
        sort: params.sort,
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
      };
    },
    ...options,
  });
}

/**
 * Every network of the workspace, page after page. A network is a partner,
 * there are tens of them, and the screens that name or filter by one need
 * all of them — never «the first page» standing in for the list.
 */
export function useAllAffiliateNetworks(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...networkKeys.all, "all"] as const,
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listAffiliateNetworks({ pagination: new Pagination({ limit, offset }) });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
  });
}

/** The networks with these ids, by id. */
export function useNetworksByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<AffiliateNetwork>(
    networkKeys.all,
    ids,
    async (chunk) => (await client.listAffiliateNetworks({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

export function useGetAffiliateNetwork(id: string | undefined) {
  return useQuery({
    queryKey: id ? networkKeys.detail(id) : ["affiliate-networks", "detail-disabled"],
    enabled: !!id,
    queryFn: async () => {
      if (!id) throw new Error("missing id");
      const res = await client.getAffiliateNetwork({ id });
      return res.network!;
    },
  });
}

// useCreateAffiliateNetwork lets the FE persist a fresh Network row. The BE
// fills in workspace_id, ownership, and the generated inbound_postback_url
// from the caller's auth context — the proto on the wire just needs the
// editable surface (name, currency, payout_model, postback_security, etc.).
export function useCreateAffiliateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (network: AffiliateNetwork) => {
      const res = await client.createAffiliateNetwork({ network });
      return res.network!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: networkKeys.all });
    },
  });
}

/**
 * Saves the partner. `replayNewlyMapped` — the status table's «Сохранить»:
 * the postbacks held on a word this save maps for the first time are applied
 * again (the server schedules at most 1000 per word).
 */
export function useUpdateAffiliateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ network, replayNewlyMapped = false }: { network: AffiliateNetwork; replayNewlyMapped?: boolean }) => {
      const res = await client.updateAffiliateNetwork({ network, replayNewlyMapped });
      return res.network!;
    },
    onSuccess: (network) => {
      void qc.invalidateQueries({ queryKey: networkKeys.all });
      if (network?.id) {
        qc.setQueryData(networkKeys.detail(network.id), network);
      }
    },
  });
}

export function useArchiveAffiliateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveAffiliateNetwork({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: networkKeys.all });
    },
  });
}

/** «Как читать статусы»: every word the partner sent in 30 days or the mapping names. */
export function useNetworkStatuses(networkId: string | undefined) {
  return useQuery({
    queryKey: [...networkKeys.all, "statuses", networkId ?? ""] as const,
    enabled: !!networkId,
    queryFn: async (): Promise<ListNetworkStatusesResponse> => client.listNetworkStatuses({ networkId: networkId ?? "" }),
  });
}

export interface NetworkNumbersParams {
  ids: string[];
  range: DayRange;
  timezone: string;
  basis: ConversionBasis;
}

/** The partners on screen over the app's period, from the report engine (ListNetworkNumbers). */
export function useNetworkNumbers(p: NetworkNumbersParams) {
  return useQuery({
    queryKey: [...networkKeys.all, "numbers", p] as const,
    enabled: p.ids.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ListNetworkNumbersResponse> =>
      client.listNetworkNumbers({
        ids: p.ids,
        dateRange: new DateRange({ from: p.range.from, to: p.range.to, timezone: p.timezone }),
        attributionBasis: basisToProto(p.basis),
      }),
  });
}
